import { ReactNode } from 'react';
import { TrendingUp, TrendingDown } from 'lucide-react';

function cn(...classes: (string | undefined | null | false)[]): string {
  return classes.filter(Boolean).join(' ');
}

interface MetricCardProps {
  title: string;
  value: string | number;
  change?: number;
  changeLabel?: string;
  icon: ReactNode;
  gradient?: boolean;
  color?: 'red' | 'emerald' | 'blue' | 'amber' | 'purple' | 'cyan' | 'slate';
  subtitle?: string;
  formatValue?: (value: number) => string;
}

const colorClasses = {
  red: {
    bg: 'from-red-500/20 to-orange-500/10',
    border: 'border-red-500/20',
    icon: 'bg-red-500/20',
    iconColor: 'text-red-400',
    change: 'text-emerald-400',
  },
  emerald: {
    bg: 'from-emerald-500/20 to-green-500/10',
    border: 'border-emerald-500/20',
    icon: 'bg-emerald-500/20',
    iconColor: 'text-emerald-400',
    change: 'text-emerald-400',
  },
  blue: {
    bg: 'from-blue-500/20 to-cyan-500/10',
    border: 'border-blue-500/20',
    icon: 'bg-blue-500/20',
    iconColor: 'text-blue-400',
    change: 'text-emerald-400',
  },
  amber: {
    bg: 'from-amber-500/20 to-yellow-500/10',
    border: 'border-amber-500/20',
    icon: 'bg-amber-500/20',
    iconColor: 'text-amber-400',
    change: 'text-emerald-400',
  },
  purple: {
    bg: 'from-purple-500/20 to-violet-500/10',
    border: 'border-purple-500/20',
    icon: 'bg-purple-500/20',
    iconColor: 'text-purple-400',
    change: 'text-emerald-400',
  },
  cyan: {
    bg: 'from-cyan-500/20 to-blue-500/10',
    border: 'border-cyan-500/20',
    icon: 'bg-cyan-500/20',
    iconColor: 'text-cyan-400',
    change: 'text-emerald-400',
  },
  slate: {
    bg: 'from-slate-500/20 to-gray-500/10',
    border: 'border-slate-500/20',
    icon: 'bg-slate-500/20',
    iconColor: 'text-slate-400',
    change: 'text-emerald-400',
  },
};

export function MetricCard({
  title,
  value,
  change,
  changeLabel,
  icon,
  gradient = false,
  color = 'red',
  subtitle,
  formatValue,
}: MetricCardProps) {
  // Validação de props obrigatórias
  if (!icon) {
    console.error('MetricCard: icon prop is required');
    return null;
  }

  // Validação de cor válida com fallback
  const colors = colorClasses[color] || colorClasses.red;
  const isPositive = change !== undefined && change >= 0;

  return (
    <div
      className={cn(
        'rounded-2xl p-6 border transition-all hover:scale-[1.02]',
        gradient
          ? `bg-gradient-to-br ${colors.bg} ${colors.border}`
          : 'bg-[#0a0a0a] border-white/10'
      )}
    >
      <div className="flex items-start justify-between mb-4">
        <div className={cn('p-3 rounded-xl', colors.icon)}>
          <div className={colors.iconColor}>{icon}</div>
        </div>
        {change !== undefined && (
          <div className={cn('flex items-center gap-1 text-sm font-medium', colors.change)}>
            {isPositive ? (
              <>
                <TrendingUp className="w-4 h-4" />
                +{Math.abs(change).toFixed(1)}%
              </>
            ) : (
              <>
                <TrendingDown className="w-4 h-4 text-red-400" />
                -{Math.abs(change).toFixed(1)}%
              </>
            )}
          </div>
        )}
      </div>
      <p className="text-sm text-slate-400 mb-1">{title}</p>
      <p className="text-3xl font-bold text-white mb-1">
        {typeof value === 'number' && formatValue ? formatValue(value) : value}
      </p>
      {subtitle && (
        <p className="text-xs text-slate-500">{subtitle}</p>
      )}
      {changeLabel && (
        <p className="text-xs text-slate-500 mt-1">{changeLabel}</p>
      )}
    </div>
  );
}
